import React, { useEffect, useRef, useState } from 'react';
import { colors } from '../styles/tokens';
import { useEsMovil } from '../hooks/useEsMovil';

/** Cuánto se muestra el label/color de error antes de revertir al estado normal. */
const ERROR_FEEDBACK_MS = 3000;

export interface ExportButtonProps {
  /** Dispara el export (normalmente `exportXlsx(sheets, filename)` del llamador) — el propio botón maneja el estado "exportando…"/error mientras la promesa resuelve. */
  onExport: () => void | Promise<void>;
  /** Deshabilitado cuando no hay datos que exportar (la vista/modal ya sabe si su data está vacía). */
  disabled?: boolean;
  /** Texto del botón — default "Excel" (subtle, cabe en un header angosto de modal). */
  label?: string;
  /** Sufijo de `data-testid` para distinguir el botón entre las 5 vistas que lo montan (ej. `vendedores`, `negados-tienda`). Default `'export-xlsx-btn'` sin sufijo. */
  testid?: string;
  /**
   * `'light'` (default) = fondo tenue sobre las tarjetas claras de Mi tienda
   * (`Vendedores`/`NegadosTiendaTable`). `'dark'` = mismo tratamiento
   * translúcido que el botón de cerrar (`×`) de los 3 modales, cuyo header
   * es un fondo de marca sólido (`colors.brand`/`colors.drill.marca`) — el
   * estilo `light` sería ilegible ahí.
   */
  variant?: 'light' | 'dark';
}

/** Ícono de descarga — SVG a mano, mismo criterio que los demás íconos del módulo (sin librería ni emoji). */
const DownloadIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3v12" />
    <path d="M7 10l5 5 5-5" />
    <path d="M4 19h16" />
  </svg>
);

/**
 * Botón "Exportar a Excel" reusado por las 5 vistas del plan
 * `2026-07-22-detalle-por-modelo-y-export-xlsx-plan` (Fase D, espejo del
 * app): sección "Vendedores del día" + tabla "Productos más negados" de Mi
 * tienda, y los headers de
 * `VendedorDetalleModal`/`ModeloDetalleModal`/`NegadosDrillModal`. Subtle a
 * propósito (fondo tenue, sin competir con el CTA principal de cada vista) —
 * icono de descarga + label corto. Deshabilitado cuando no hay data
 * (`disabled`, decide el llamador). Mientras `onExport` resuelve, se
 * deshabilita a sí mismo y muestra "Exportando…" para evitar doble-clic (la
 * carga del `import()` dinámico de `xlsx`, ver `utils/exportXlsx.ts`, puede
 * tardar un instante en redes lentas). Si `onExport` rechaza, muestra "Error
 * al exportar" en rojo (`colors.rojo`, `data-testid="export-xlsx-error"`) por
 * ~3s y luego revierte al label normal — el botón se re-habilita en ambos
 * casos (éxito y error), nunca queda deshabilitado permanentemente.
 */
const ExportButton: React.FC<ExportButtonProps> = ({
  onExport,
  disabled = false,
  label = 'Excel',
  testid,
  variant = 'light',
}) => {
  const esMovil = useEsMovil();
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(false);
  const errorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const finalTestId = testid ? `export-xlsx-btn-${testid}` : 'export-xlsx-btn';
  const isDisabled = disabled || exporting;
  const iconColor = error ? colors.rojo : variant === 'dark' ? '#fff' : colors.texto.secondary;

  // Limpia el timer pendiente al desmontar — evita el setState-after-unmount
  // si el componente (ej. un modal) se cierra antes de que pasen los 3s.
  useEffect(() => {
    return () => {
      if (errorTimerRef.current) {
        clearTimeout(errorTimerRef.current);
      }
    };
  }, []);

  async function handleClick() {
    if (isDisabled) return;
    if (errorTimerRef.current) {
      clearTimeout(errorTimerRef.current);
      errorTimerRef.current = null;
    }
    setError(false);
    setExporting(true);
    try {
      await onExport();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error exportando a Excel', err);
      setError(true);
      errorTimerRef.current = setTimeout(() => {
        setError(false);
        errorTimerRef.current = null;
      }, ERROR_FEEDBACK_MS);
    } finally {
      setExporting(false);
    }
  }

  /**
   * Altura tocable en telefono (regla 4 de la adaptacion a movil: >= 44 px).
   * En escritorio el boton mide ~28 px y asi se queda —es deliberadamente
   * discreto, no compite con el CTA de cada vista—, pero ese mismo tamano en
   * un telefono es inalcanzable para un dedo.
   *
   * SE ARREGLA AQUI, NO EN CADA VISTA: este boton lo montan OCHO sitios (las
   * 5 vistas del tablero y los 3 modales de detalle). Tres de ellos lo habian
   * envuelto en un flex con `alignItems:'stretch'` para estirarlo sin tocar
   * este archivo; con la altura propia esos envoltorios siguen funcionando
   * (estirar algo que ya mide 44 px lo deja en 44) y los otros cinco sitios
   * —incluidos los modales— dejan de quedarse fuera de la regla.
   */
  const tocableMovil: React.CSSProperties = esMovil
    ? { minHeight: 44, padding: '11px 14px', fontSize: 12.5 }
    : {};

  return (
    <button
      type="button"
      data-testid={finalTestId}
      onClick={handleClick}
      disabled={isDisabled}
      title={error ? 'Error al exportar' : 'Exportar a Excel'}
      aria-label={error ? 'Error al exportar' : 'Exportar a Excel'}
      style={
        variant === 'dark'
          ? {
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background: 'rgba(255,255,255,.14)',
              border: 'none',
              borderRadius: 8,
              color: '#fff',
              fontSize: 11.5,
              fontWeight: 700,
              padding: '6px 11px',
              cursor: isDisabled ? 'default' : 'pointer',
              opacity: isDisabled ? 0.5 : 1,
              flex: 'none',
              whiteSpace: 'nowrap',
              ...tocableMovil,
            }
          : {
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background: colors.chipBg,
              border: `1px solid ${colors.border}`,
              borderRadius: 8,
              color: colors.texto.secondary,
              fontSize: 11.5,
              fontWeight: 700,
              padding: '6px 11px',
              cursor: isDisabled ? 'default' : 'pointer',
              opacity: isDisabled ? 0.5 : 1,
              flex: 'none',
              whiteSpace: 'nowrap',
              ...tocableMovil,
            }
      }
    >
      <DownloadIcon color={iconColor} />
      {error ? (
        <span data-testid="export-xlsx-error" style={{ color: colors.rojo }}>
          Error al exportar
        </span>
      ) : exporting ? (
        'Exportando…'
      ) : (
        label
      )}
    </button>
  );
};

export default ExportButton;
